import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Edit3, ExternalLink, FileText,
  Loader2, RefreshCw, Sparkles, XCircle,
} from 'lucide-react';
import Card from '../components/Card';
import { SkeletonDetail } from '../components/Skeleton';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../context/AuthContext';
import {
  decideReview, getReview, rerunReviewTriage, reviewDocumentUrl, withdrawReview,
} from '../features/reviews/reviewsApi';
import { CLASS_TONE, ReviewStatusChip, TriageChip } from '../features/reviews/reviewBits';
import { fmtDateTimeOrEmpty as formatDateTime } from '../utils/format';

const OPEN = ['pending_review', 'changes_requested'];

function Subhead({ children }) {
  return (
    <div className="muted" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
      {children}
    </div>
  );
}

// The advisory AI triage card — classification, summary, checklist, redlines.
function TriageCard({ review, canReview, onRerun, busy }) {
  const ai = review.aiReview;
  const status = review.aiStatus;
  return (
    <Card
      title="AI first-pass"
      icon={Sparkles}
      action={canReview && ['complete', 'failed'].includes(status) ? (
        <button className="btn outline sm" onClick={onRerun} disabled={busy}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Rerun
        </button>
      ) : null}
    >
      <div className="stack-md">
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          Advisory — a machine read of the document against Lightspark's playbook. It approves
          nothing; the legal sign-off below is the decision.
        </div>
        {(status === 'queued' || status === 'running') && (
          <div className="row muted" style={{ gap: 8, fontSize: 13 }}><Loader2 className="w-4 h-4 animate-spin" />Reading the document…</div>
        )}
        {status === 'failed' && (
          <div className="callout yellow"><div className="callout-body" style={{ fontSize: 12 }}>
            Couldn't read this document automatically{review.aiError ? ` (${review.aiError})` : ''}. Legal will review it directly.
          </div></div>
        )}
        {status === 'complete' && ai && (
          <>
            <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <TriageChip aiStatus={status} classification={review.aiClassification} />
              {ai.ndaType && <span className="muted" style={{ fontSize: 12 }}>{ai.ndaType}</span>}
              {review.aiGeneratedAt && <span className="muted mono" style={{ fontSize: 11, marginLeft: 'auto' }}>{formatDateTime(review.aiGeneratedAt)}</span>}
            </div>
            {ai.executiveSummary && <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{ai.executiveSummary}</div>}
            {ai.recommendedAction && (
              <div style={{ fontSize: 12.5 }}><strong>Recommended:</strong> {ai.recommendedAction}</div>
            )}
            {Array.isArray(ai.keyRedlines) && ai.keyRedlines.length > 0 && (
              <div>
                <Subhead>Key redlines</Subhead>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                  {ai.keyRedlines.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(ai.checklist) && ai.checklist.length > 0 && (
              <div style={{ paddingTop: 12, borderTop: '0.5px solid var(--border-primary)' }}>
                <Subhead>Checklist</Subhead>
                <div style={{ display: 'grid', gap: 8 }}>
                  {ai.checklist.map((item, i) => {
                    const tone = CLASS_TONE[item.classification] || CLASS_TONE.YELLOW;
                    return (
                      <div key={i} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                        <span className="chip" style={{ background: tone.bg, color: tone.fg, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                          {item.classification}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-primary)' }}>{item.section}</div>
                          {item.findings && <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{item.findings}</div>}
                          {(item.recommendations || []).map((rec, j) => (
                            <div key={j} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>↳ {rec}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export default function ReviewDetail() {
  const { id } = useParams();
  const { showToast } = useToast();
  const { user, canWriteContracts } = useAuth();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  const refresh = () => getReview(id).then(setReview).catch((e) => setError(e.message));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getReview(id)
      .then((r) => { if (!cancelled) { setReview(r); setError(null); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  // Poll while triage is still running so the card fills in live.
  useEffect(() => {
    if (!review || !['queued', 'running'].includes(review.aiStatus)) return undefined;
    const t = setInterval(() => { refresh(); }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review?.aiStatus, id]);

  const decide = async (action) => {
    setBusy(true);
    try {
      const updated = await decideReview(id, { action, notes: notes.trim() || undefined });
      setReview(updated);
      setNotes('');
      showToast(action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Changes requested', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const rerun = async () => {
    setBusy(true);
    try { setReview(await rerunReviewTriage(id)); } catch (err) { showToast(err.message, 'error'); } finally { setBusy(false); }
  };

  const onWithdraw = async () => {
    if (!window.confirm('Withdraw this review request?')) return;
    setBusy(true);
    try { setReview(await withdrawReview(id)); showToast('Withdrawn', 'success'); }
    catch (err) { showToast(err.message, 'error'); } finally { setBusy(false); }
  };

  if (loading) return <SkeletonDetail />;
  if (error) {
    return (
      <div className="canvas-inner animate-fade-in">
        <div className="callout red" style={{ padding: '16px 20px' }}>
          <div className="callout-body">
            <div style={{ fontSize: 13, fontWeight: 500 }}>{error}</div>
            <Link to="/reviews" className="btn outline sm" style={{ marginTop: 12 }}><ArrowLeft className="w-3.5 h-3.5" />Back to reviews</Link>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = review.requesterId === user?.id;
  const isOpen = OPEN.includes(review.status);
  const canSignOff = canWriteContracts && isOpen;

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="row" style={{ gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
        <Link to="/reviews" className="row" style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}>
          <ArrowLeft className="w-3 h-3" />Reviews
        </Link>
      </div>

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div className="row" style={{ gap: 12, minWidth: 0 }}>
          <FileText className="w-6 h-6" style={{ color: 'var(--icon-secondary)', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <h1 className="page-title" style={{ fontSize: 22, marginBottom: 4 }}>{review.title || review.filename}</h1>
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <ReviewStatusChip status={review.status} />
              {review.documentType === 'nda' && <span className="chip gray">NDA</span>}
              <span className="muted" style={{ fontSize: 12.5 }}>from {review.requesterName || '—'} · {formatDateTime(review.createdAt)}</span>
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <a href={reviewDocumentUrl(review.id)} target="_blank" rel="noreferrer" className="btn outline">
            <ExternalLink className="w-4 h-4" />Open document
          </a>
          {isOwner && isOpen && (
            <button className="btn outline" onClick={onWithdraw} disabled={busy}>Withdraw</button>
          )}
        </div>
      </div>

      <div className="stack-lg">
        <Card title="Legal sign-off" icon={CheckCircle}>
          <div className="stack-md">
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <ReviewStatusChip status={review.status} />
              {review.reviewedByName && (
                <span className="muted" style={{ fontSize: 12.5 }}>by {review.reviewedByName} · {formatDateTime(review.reviewedAt)}</span>
              )}
            </div>
            {review.reviewNotes && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{review.reviewNotes}</div>
            )}
            {canSignOff ? (
              <>
                <textarea
                  className="textarea"
                  placeholder="Notes for the requester (optional, required-ish for changes/reject)…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  style={{ width: '100%', resize: 'vertical', padding: '10px 12px' }}
                />
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn primary sm" disabled={busy} onClick={() => decide('approve')}>
                    <CheckCircle className="w-3.5 h-3.5" />Approve
                  </button>
                  <button className="btn outline sm" disabled={busy} onClick={() => decide('request_changes')}>
                    <Edit3 className="w-3.5 h-3.5" />Request changes
                  </button>
                  <button className="btn outline sm" disabled={busy} onClick={() => decide('reject')}>
                    <XCircle className="w-3.5 h-3.5" />Reject
                  </button>
                </div>
              </>
            ) : (
              !isOpen && <div className="muted" style={{ fontSize: 12.5 }}>This review is closed.</div>
            )}
          </div>
        </Card>

        <TriageCard review={review} canReview={canWriteContracts} onRerun={rerun} busy={busy} />

        {review.history?.length > 0 && (
          <Card title="History" icon={FileText}>
            <div style={{ display: 'grid', gap: 8 }}>
              {review.history.map((h) => (
                <div key={h.id} className="row" style={{ gap: 10, alignItems: 'baseline', fontSize: 12.5 }}>
                  <span className="muted mono" style={{ fontSize: 11, flexShrink: 0, width: 130 }}>{formatDateTime(h.createdAt)}</span>
                  <span style={{ color: 'var(--text-primary)' }}>{h.note || h.newStatus}{h.changedByName ? ` — ${h.changedByName}` : ''}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
